import { useEffect, useRef, type RefObject } from 'react'

type DragState = {
  pointerId: number
  startX: number
  startOffset: number
  dragging: boolean
}

export type UseInfiniteMarqueeOptions = {
  loopSeconds?: number
  draggingClassName?: string
  pauseOnHover?: boolean
}

/** GPU-smooth infinite horizontal marquee with drag (loop = two duplicated tracks). */
export function useInfiniteMarquee(
  enabled: boolean,
  containerRef: RefObject<HTMLElement | null>,
  trackRef: RefObject<HTMLElement | null>,
  options: UseInfiniteMarqueeOptions = {},
) {
  const {
    loopSeconds = 80,
    draggingClassName = 'vc-infinite-marquee--dragging',
    pauseOnHover = true,
  } = options

  const offsetRef = useRef(0)
  const pauseRef = useRef(false)
  const rafRef = useRef(0)
  const resumeTimerRef = useRef<number | null>(null)
  const dragRef = useRef<DragState>({
    pointerId: -1,
    startX: 0,
    startOffset: 0,
    dragging: false,
  })
  const dragEndedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    const el = containerRef.current
    const track = trackRef.current
    if (!el || !track) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const dragThresholdPx = 10

    const loopHalfWidth = () => {
      const first = track.firstElementChild
      const second = first?.nextElementSibling ?? null
      if (first instanceof HTMLElement && second instanceof HTMLElement) {
        const span = second.offsetLeft - first.offsetLeft
        if (span > 0) return span
      }
      if (first instanceof HTMLElement && first.scrollWidth > 0) {
        return first.scrollWidth
      }
      const half = track.scrollWidth / 2
      return half > 0 ? half : 0
    }

    const normalizeOffset = (value: number, half: number) => {
      if (half <= 0) return 0
      let next = value % half
      if (next < 0) next += half
      return next
    }

    const applyOffset = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`
    }

    const pauseAuto = () => {
      pauseRef.current = true
      if (resumeTimerRef.current != null) {
        window.clearTimeout(resumeTimerRef.current)
        resumeTimerRef.current = null
      }
    }

    const scheduleResumeAuto = () => {
      if (resumeTimerRef.current != null) {
        window.clearTimeout(resumeTimerRef.current)
      }
      resumeTimerRef.current = window.setTimeout(() => {
        resumeTimerRef.current = null
        pauseRef.current = false
      }, 1200)
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button > 0) return
      pauseAuto()
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startOffset: offsetRef.current,
        dragging: false,
      }
    }

    const onPointerMove = (e: PointerEvent) => {
      const drag = dragRef.current
      if (drag.pointerId < 0 || e.pointerId !== drag.pointerId) return
      const dx = e.clientX - drag.startX
      if (!drag.dragging) {
        if (Math.abs(dx) <= dragThresholdPx) return
        drag.dragging = true
        pauseAuto()
        el.classList.add(draggingClassName)
        el.setPointerCapture(e.pointerId)
      }
      e.preventDefault()
      const half = loopHalfWidth()
      offsetRef.current = normalizeOffset(drag.startOffset - dx, half)
      applyOffset()
    }

    const onPointerUp = (e: PointerEvent) => {
      const drag = dragRef.current
      if (drag.pointerId < 0 || e.pointerId !== drag.pointerId) return
      if (drag.dragging) {
        e.preventDefault()
        dragEndedRef.current = true
        if (el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId)
        }
        el.classList.remove(draggingClassName)
      }
      scheduleResumeAuto()
      dragRef.current = {
        pointerId: -1,
        startX: 0,
        startOffset: offsetRef.current,
        dragging: false,
      }
    }

    el.addEventListener('pointerdown', onPointerDown, { passive: true })
    el.addEventListener('pointermove', onPointerMove, { passive: false })
    el.addEventListener('pointerup', onPointerUp, { passive: false })
    el.addEventListener('pointercancel', onPointerUp, { passive: false })

    const onMouseEnter = () => pauseAuto()
    const onMouseLeave = () => scheduleResumeAuto()
    if (pauseOnHover) {
      el.addEventListener('mouseenter', onMouseEnter)
      el.addEventListener('mouseleave', onMouseLeave)
    }

    let lastTs = performance.now()
    applyOffset()

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            const half = loopHalfWidth()
            if (half > 0) {
              offsetRef.current = normalizeOffset(offsetRef.current, half)
              applyOffset()
            }
          })
        : null
    ro?.observe(track)
    for (const child of track.children) {
      if (child instanceof HTMLElement) ro?.observe(child)
    }

    const tick = (now: number) => {
      const dt = Math.min(0.032, (now - lastTs) / 1000)
      lastTs = now

      const dragging = dragRef.current.dragging
      if (!reducedMotion && !pauseRef.current && !dragging) {
        const half = loopHalfWidth()
        if (half > 0) {
          const pxPerSec = half / loopSeconds
          offsetRef.current += pxPerSec * dt
          if (offsetRef.current >= half) {
            offsetRef.current -= half
          }
          applyOffset()
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      ro?.disconnect()
      cancelAnimationFrame(rafRef.current)
      if (resumeTimerRef.current != null) {
        window.clearTimeout(resumeTimerRef.current)
      }
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerUp)
      if (pauseOnHover) {
        el.removeEventListener('mouseenter', onMouseEnter)
        el.removeEventListener('mouseleave', onMouseLeave)
      }
      el.classList.remove(draggingClassName)
      track.style.transform = ''
      pauseRef.current = false
      dragRef.current = {
        pointerId: -1,
        startX: 0,
        startOffset: 0,
        dragging: false,
      }
    }
  }, [enabled, containerRef, trackRef, loopSeconds, draggingClassName, pauseOnHover])

  return { dragEndedRef }
}
